import { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Send, RotateCcw, ChevronRight, Bot, Sparkles, Database } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { format } from 'date-fns';

const GOLD   = '#b07d1a';
const RED    = '#DC2626';
const GREEN  = '#16A34A';
const ORANGE = '#D97706';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  timestamp: Date;
  isData?: boolean;
  actions?: { label: string; route?: string; query?: string }[];
}

// ── Data-query intent handlers ────────────────────────────────────────────────
type DataResult = { text: string; actions?: Message['actions'] };

interface CapaRecord { id: string; capaCode: string; title: string; severity: string; status: string; dueDate: string; labId?: string; }
interface CertRecord { id: string; certCode?: string; name: string; labId: string; status: string; expiryDate?: string; validUntil?: string; certificationBody?: string; }
interface AuditRecord { id: string; auditCode: string; type: string; status: string; score: number; scheduledDate: string; lab?: { name: string }; labName?: string; }
interface BatchRecord { id: string; batchNo: string; productName: string; status: string; yieldPercent?: number; scheduledDate?: string; releaseDate?: string; deviationsCount?: number; lab?: { name: string }; }
interface SopRecord  { id: string; sopCode: string; title: string; status: string; reviewDate: string; department?: string; }
interface LabRecord  { id: string; name: string; type: string; city?: string; overallScore?: number; riskScore?: number; }
interface NotifRecord{ id: string; title: string; message: string; type: string; isRead: boolean; createdAt: string; }
interface DocRecord  { id: string; docCode: string; title: string; type: string; status: string; department?: string; fileFormat?: string; }
interface EventRecord{ id: string; type: string; title: string; subtitle: string; date: string; urgency: string; daysLeft?: number; }
interface KpiData    { value: number; trend?: number; trendDir?: string; }

const DATA_INTENTS: {
  patterns: RegExp[];
  fetch: () => Promise<DataResult>;
}[] = [
  // ── Open / overdue CAPAs ──────────────────────────────────────────────────
  {
    patterns: [/\bopen\s+capa|\bcapa.*open|how many.*capa|open.*devia|active.*capa/i],
    fetch: async () => {
      const r = await api.get('/capa', { params: { status: 'open', limit: 10 } });
      const items: CapaRecord[] = r.data?.data ?? [];
      if (!items.length) return { text: 'No open CAPAs found right now. ✅' };
      const lines = items.map((c) => {
        const days = Math.ceil((new Date(c.dueDate).getTime() - Date.now()) / 86400000);
        const flag = days < 0 ? `⚠️ ${Math.abs(days)}d overdue` : days <= 7 ? `🔴 ${days}d left` : `${days}d left`;
        return `• **${c.capaCode}** — ${c.title} (${c.severity}) · ${flag}`;
      });
      return {
        text: `**${items.length} Open CAPAs** (showing up to 10):\n\n${lines.join('\n')}`,
        actions: [{ label: 'View all open CAPAs', route: '/capa?status=open' }],
      };
    },
  },
  {
    patterns: [/\boverdue\s+(capa|deviation)|capa.*overdue|late.*capa/i],
    fetch: async () => {
      const r = await api.get('/capa', { params: { status: 'overdue', limit: 10 } });
      const items: CapaRecord[] = r.data?.data ?? [];
      if (!items.length) return { text: 'No overdue CAPAs right now. ✅' };
      const lines = items.map((c) => {
        const days = Math.abs(Math.ceil((new Date(c.dueDate).getTime() - Date.now()) / 86400000));
        return `• **${c.capaCode}** — ${c.title} · ⛔ ${days}d overdue`;
      });
      return {
        text: `**${items.length} Overdue CAPAs:**\n\n${lines.join('\n')}`,
        actions: [{ label: 'View overdue CAPAs', route: '/capa?status=overdue' }],
      };
    },
  },
  {
    patterns: [/\bcritical\s+(capa|deviation)|capa.*critical/i],
    fetch: async () => {
      const r = await api.get('/capa', { params: { severity: 'critical', limit: 10 } });
      const items: CapaRecord[] = r.data?.data ?? [];
      const critical = items.filter((c) => c.severity === 'critical' || c.severity === 'Critical');
      if (!critical.length) return { text: 'No critical CAPAs found. ✅' };
      const lines = critical.map((c) => `• **${c.capaCode}** — ${c.title} (${c.status})`);
      return {
        text: `**${critical.length} Critical CAPAs:**\n\n${lines.join('\n')}`,
        actions: [{ label: 'CAPA module', route: '/capa' }],
      };
    },
  },

  // ── Certifications ────────────────────────────────────────────────────────
  {
    patterns: [/expir(ing|ed|ation).*cert|cert.*expir|renewal.*(due|alert)|cert.*alert/i],
    fetch: async () => {
      const r = await api.get('/certifications', { params: { status: 'expired,renewal_due', limit: 15 } });
      const items: CertRecord[] = r.data?.data ?? [];
      const expired    = items.filter((c) => c.status === 'expired');
      const renewalDue = items.filter((c) => c.status === 'renewal_due');
      if (!items.length) return { text: 'All certifications are valid. ✅' };
      const lines: string[] = [];
      expired.forEach((c) => lines.push(`• 🔴 **EXPIRED** — ${c.name} (${c.certificationBody ?? ''})`));
      renewalDue.forEach((c) => {
        const exp = c.expiryDate || c.validUntil;
        const daysLeft = exp ? Math.ceil((new Date(exp).getTime() - Date.now()) / 86400000) : null;
        lines.push(`• 🟠 **Renewal Due** — ${c.name}${daysLeft !== null ? ` · ${daysLeft}d left` : ''}`);
      });
      return {
        text: `**${expired.length} Expired + ${renewalDue.length} Renewal Due:**\n\n${lines.join('\n')}`,
        actions: [
          { label: 'View expired', route: '/certifications?status=expired' },
          { label: 'View renewal due', route: '/certifications?status=renewal_due' },
        ],
      };
    },
  },
  {
    patterns: [/\bvalid\s+cert|cert.*valid|all.*cert|list.*cert/i],
    fetch: async () => {
      const r = await api.get('/certifications', { params: { limit: 20 } });
      const items: CertRecord[] = r.data?.data ?? [];
      const valid      = items.filter((c) => c.status === 'valid').length;
      const renewalDue = items.filter((c) => c.status === 'renewal_due').length;
      const expired    = items.filter((c) => c.status === 'expired').length;
      return {
        text: `**Certification Summary** (${items.length} total):\n\n• ✅ Valid: **${valid}**\n• 🟠 Renewal Due: **${renewalDue}**\n• 🔴 Expired: **${expired}**`,
        actions: [{ label: 'View Certifications', route: '/certifications' }],
      };
    },
  },

  // ── Audits ────────────────────────────────────────────────────────────────
  {
    patterns: [/recent.*audit|latest.*audit|last.*audit|audit.*recent|audit.*score|audit.*result/i],
    fetch: async () => {
      const r = await api.get('/audits', { params: { limit: 8 } });
      const items: AuditRecord[] = r.data?.data ?? [];
      if (!items.length) return { text: 'No audit records found.' };
      const lines = items.map((a) => {
        const labName = a.lab?.name ?? a.labName ?? '—';
        const scoreColor = a.score >= 80 ? '✅' : a.score >= 60 ? '🟠' : '🔴';
        return `• ${scoreColor} **${a.auditCode}** — ${labName} · ${a.type} · Score: **${a.score}%** (${format(new Date(a.scheduledDate), 'd MMM yyyy')})`;
      });
      return {
        text: `**Recent Audits** (latest ${items.length}):\n\n${lines.join('\n')}`,
        actions: [{ label: 'Audit Tracker', route: '/audits' }],
      };
    },
  },
  {
    patterns: [/fail.*audit|audit.*fail|audit.*below|low.*audit|audit.*poor/i],
    fetch: async () => {
      const r = await api.get('/audits', { params: { limit: 20 } });
      const items: AuditRecord[] = r.data?.data ?? [];
      const failed = items.filter((a) => a.score < 80);
      if (!failed.length) return { text: 'All recent audits scored ≥ 80 (GMP compliant). ✅' };
      const lines = failed.map((a) => {
        const labName = a.lab?.name ?? a.labName ?? '—';
        return `• 🔴 **${a.auditCode}** — ${labName} · Score: **${a.score}%** · ${a.type}`;
      });
      return {
        text: `**${failed.length} Audits below GMP threshold (< 80%):**\n\n${lines.join('\n')}`,
        actions: [{ label: 'Audit Tracker', route: '/audits' }],
      };
    },
  },
  {
    patterns: [/scheduled.*audit|upcoming.*audit|planned.*audit/i],
    fetch: async () => {
      const r = await api.get('/audits', { params: { tab: 'scheduled', limit: 8 } });
      const items: AuditRecord[] = r.data?.data ?? [];
      const scheduled = items.filter((a) => a.status === 'scheduled');
      if (!scheduled.length) return { text: 'No scheduled audits found.' };
      const lines = scheduled.map((a) => {
        const labName = a.lab?.name ?? a.labName ?? '—';
        return `• 📅 **${a.auditCode}** — ${labName} · ${a.type} · ${format(new Date(a.scheduledDate), 'd MMM yyyy')}`;
      });
      return {
        text: `**${scheduled.length} Scheduled Audits:**\n\n${lines.join('\n')}`,
        actions: [{ label: 'Audit Tracker', route: '/audits' }],
      };
    },
  },

  // ── Batches ───────────────────────────────────────────────────────────────
  {
    patterns: [/batch.*hold|hold.*batch|qc.?hold/i],
    fetch: async () => {
      const r = await api.get('/batches', { params: { status: 'qc_hold', limit: 10 } });
      const items: BatchRecord[] = r.data?.data ?? [];
      if (!items.length) return { text: 'No batches currently on QC hold. ✅' };
      const lines = items.map((b) => {
        const lab = b.lab?.name ?? '—';
        return `• 🔴 **${b.batchNo}** — ${b.productName} · ${lab}${b.deviationsCount ? ` · ${b.deviationsCount} dev.` : ''}`;
      });
      return {
        text: `**${items.length} Batches on QC Hold:**\n\n${lines.join('\n')}`,
        actions: [{ label: 'View QC Hold batches', route: '/bmr?status=qc_hold' }],
      };
    },
  },
  {
    patterns: [/recent.*batch|latest.*batch|batch.*releas|released.*batch/i],
    fetch: async () => {
      const r = await api.get('/batches', { params: { status: 'released', limit: 8 } });
      const items: BatchRecord[] = r.data?.data ?? [];
      if (!items.length) return { text: 'No recently released batches found.' };
      const lines = items.map((b) => {
        const yld = b.yieldPercent != null ? ` · Yield: **${b.yieldPercent.toFixed(1)}%**` : '';
        return `• ✅ **${b.batchNo}** — ${b.productName}${yld}`;
      });
      return {
        text: `**${items.length} Recently Released Batches:**\n\n${lines.join('\n')}`,
        actions: [{ label: 'Batch Records', route: '/bmr' }],
      };
    },
  },
  {
    patterns: [/all.*batch|list.*batch|batch.*list|batch.*summar/i],
    fetch: async () => {
      const r = await api.get('/batches', { params: { limit: 20 } });
      const items: BatchRecord[] = r.data?.data ?? [];
      const released  = items.filter((b) => b.status === 'released').length;
      const onHold    = items.filter((b) => b.status === 'qc_hold').length;
      const pending   = items.filter((b) => b.status === 'pending' || b.status === 'in_process').length;
      const rejected  = items.filter((b) => b.status === 'rejected').length;
      return {
        text: `**Batch Records Summary** (${items.length} total):\n\n• ✅ Released: **${released}**\n• 🔴 QC Hold: **${onHold}**\n• 🔵 In Progress: **${pending}**\n• ⛔ Rejected: **${rejected}**`,
        actions: [{ label: 'View all batches', route: '/bmr' }],
      };
    },
  },

  // ── SOPs ──────────────────────────────────────────────────────────────────
  {
    patterns: [/sop.*due|due.*sop|review.*due.*sop|sop.*overdue|overdue.*sop/i],
    fetch: async () => {
      const r = await api.get('/sops/due-for-review');
      const items: SopRecord[] = r.data?.data ?? [];
      if (!items.length) return { text: 'No SOPs due for review within 90 days. ✅' };
      const lines = items.slice(0, 10).map((s) => {
        const days = Math.ceil((new Date(s.reviewDate).getTime() - Date.now()) / 86400000);
        const flag = days < 0 ? `⚠️ ${Math.abs(days)}d overdue` : `${days}d remaining`;
        return `• **${s.sopCode}** — ${s.title.substring(0, 55)}… · ${flag}`;
      });
      return {
        text: `**${items.length} SOPs due for review** (within 90 days):\n\n${lines.join('\n')}`,
        actions: [{ label: 'SOP Management', route: '/sop' }],
      };
    },
  },
  {
    patterns: [/list.*sop|all.*sop|sop.*list|active.*sop|how many.*sop/i],
    fetch: async () => {
      const r = await api.get('/sops', { params: { limit: 50 } });
      const items: SopRecord[] = r.data?.data ?? [];
      const active      = items.filter((s) => s.status === 'active').length;
      const underReview = items.filter((s) => s.status === 'under_review').length;
      const draft       = items.filter((s) => s.status === 'draft').length;
      return {
        text: `**SOP Summary** (${items.length} total):\n\n• ✅ Active: **${active}**\n• 🔵 Under Review: **${underReview}**\n• 📝 Draft: **${draft}**`,
        actions: [{ label: 'SOP Management', route: '/sop' }],
      };
    },
  },

  // ── Labs ──────────────────────────────────────────────────────────────────
  {
    patterns: [/list.*lab|all.*lab|lab.*list|how many.*lab|show.*lab/i],
    fetch: async () => {
      const r = await api.get('/labs/all');
      const items: LabRecord[] = r.data?.data ?? [];
      const internal = items.filter((l) => l.type === 'internal').length;
      const partner  = items.filter((l) => l.type === 'partner').length;
      const lines = items.slice(0, 8).map((l) => {
        const score = l.overallScore != null ? ` · Score: ${Math.round(l.overallScore)}%` : '';
        const risk  = l.riskScore   != null ? ` · Risk: ${Math.round(l.riskScore)}` : '';
        return `• **${l.name}** (${l.type})${l.city ? ` · ${l.city}` : ''}${score}${risk}`;
      });
      return {
        text: `**${items.length} Labs** (${internal} internal, ${partner} partner):\n\n${lines.join('\n')}${items.length > 8 ? `\n…and ${items.length - 8} more` : ''}`,
        actions: [{ label: 'Lab Registry', route: '/registry' }],
      };
    },
  },
  {
    patterns: [/high.risk.*lab|lab.*high.risk|risky.*lab/i],
    fetch: async () => {
      const r = await api.get('/labs/all');
      const items: LabRecord[] = r.data?.data ?? [];
      const highRisk = items.filter((l) => (l.riskScore ?? 0) >= 70).sort((a, b) => (b.riskScore ?? 0) - (a.riskScore ?? 0));
      if (!highRisk.length) return { text: 'No labs currently have a high risk score. ✅' };
      const lines = highRisk.map((l) => `• 🔴 **${l.name}** · Risk Score: **${Math.round(l.riskScore ?? 0)}**`);
      return {
        text: `**${highRisk.length} High-Risk Labs** (score ≥ 70):\n\n${lines.join('\n')}`,
        actions: [{ label: 'Lab Registry', route: '/registry' }],
      };
    },
  },

  // ── Documents ─────────────────────────────────────────────────────────────
  {
    patterns: [/recent.*doc|latest.*doc|new.*doc|list.*doc|all.*doc|how many.*doc/i],
    fetch: async () => {
      const r = await api.get('/documents', { params: { limit: 20 } });
      const items: DocRecord[] = r.data?.data ?? [];
      const approved    = items.filter((d) => d.status === 'approved').length;
      const draft       = items.filter((d) => d.status === 'draft').length;
      const underReview = items.filter((d) => d.status === 'under_review').length;
      const archived    = items.filter((d) => d.status === 'archived').length;
      return {
        text: `**Document Repository** (${r.data?.meta?.total ?? items.length} total):\n\n• ✅ Approved: **${approved}**\n• 📝 Draft: **${draft}**\n• 🔵 Under Review: **${underReview}**\n• 📦 Archived: **${archived}**`,
        actions: [{ label: 'Document Management', route: '/dms' }],
      };
    },
  },
  {
    patterns: [/draft.*doc|doc.*draft|unapproved.*doc/i],
    fetch: async () => {
      const r = await api.get('/documents', { params: { status: 'draft', limit: 10 } });
      const items: DocRecord[] = r.data?.data ?? [];
      if (!items.length) return { text: 'No draft documents found. ✅' };
      const lines = items.map((d) => `• 📝 **${d.docCode}** — ${d.title} (${d.type})`);
      return {
        text: `**${items.length} Draft Documents:**\n\n${lines.join('\n')}`,
        actions: [{ label: 'View DMS', route: '/dms' }],
      };
    },
  },

  // ── Upcoming events ───────────────────────────────────────────────────────
  {
    patterns: [/upcoming|schedule|calendar|next.*event|event.*next|due.*soon|deadline/i],
    fetch: async () => {
      const r = await api.get('/dashboard/upcoming');
      const items: EventRecord[] = r.data?.data ?? [];
      if (!items.length) return { text: 'No upcoming events in the next 30 days. ✅' };
      const urgEmoji: Record<string, string> = { critical: '🔴', high: '🟠', medium: '🔵', low: '🟢' };
      const lines = items.map((e) => {
        const emoji = urgEmoji[e.urgency] ?? '•';
        const days  = e.daysLeft != null ? ` (${e.daysLeft === 0 ? 'today' : `${Math.abs(e.daysLeft)}d${e.daysLeft < 0 ? ' ago' : ''}`})` : '';
        return `${emoji} **${e.title}**${days}`;
      });
      return {
        text: `**${items.length} Upcoming Events (next 30 days):**\n\n${lines.join('\n')}`,
        actions: [{ label: 'Dashboard', route: '/dashboard' }],
      };
    },
  },

  // ── Dashboard KPIs ────────────────────────────────────────────────────────
  {
    patterns: [/kpi|dashboard.*stat|overall.*stat|platform.*stat|summar.*platform|overview/i],
    fetch: async () => {
      const [kpiRes, certRes] = await Promise.all([
        api.get('/dashboard/kpis'),
        api.get('/dashboard/cert-health'),
      ]);
      const k = kpiRes.data?.data ?? {};
      const c = certRes.data?.data ?? {};
      const kpiLine = (key: string, label: string, emoji: string) => {
        const v = (k[key] as KpiData)?.value ?? '—';
        return `${emoji} ${label}: **${v}**`;
      };
      return {
        text: `**Platform Summary:**\n\n${kpiLine('activeLabs', 'Active Labs', '🏭')} ${kpiLine('openCapas', 'Open CAPAs', '⚠️')}\n${kpiLine('certAlerts', 'Cert Alerts', '🏅')} ${kpiLine('batchesOnHold', 'Batches on Hold', '📦')}\n${kpiLine('pendingRequests', 'Pending Requests', '🔬')} ${kpiLine('partnerLabs', 'Partner Labs', '🤝')}\n\n**Certification Health:**\n• ✅ Valid: **${c.valid ?? '—'}** · 🟠 Renewal Due: **${c.renewalDue ?? '—'}** · 🔴 Expired: **${c.expired ?? '—'}**`,
        actions: [{ label: 'Executive Dashboard', route: '/dashboard' }],
      };
    },
  },

  // ── Notifications ─────────────────────────────────────────────────────────
  {
    patterns: [/notification|unread|alert.*recent|recent.*alert/i],
    fetch: async () => {
      const r = await api.get('/notifications');
      const items: NotifRecord[] = r.data?.data ?? [];
      const unread = items.filter((n) => !n.isRead);
      if (!items.length) return { text: 'No notifications found.' };
      const lines = unread.slice(0, 8).map((n) => `• 🔔 **${n.title}** — ${n.message}`);
      return {
        text: `**${unread.length} Unread Notifications** (of ${items.length} total):\n\n${lines.length ? lines.join('\n') : 'All notifications have been read. ✅'}`,
        actions: [{ label: 'View Notifications', route: '/notifications' }],
      };
    },
  },

  // ── Partners / scorecard ──────────────────────────────────────────────────
  {
    patterns: [/partner.*list|list.*partner|all.*partner|how many.*partner/i],
    fetch: async () => {
      const r = await api.get('/partners', { params: { limit: 20 } });
      const items = r.data?.data ?? [];
      if (!items.length) return { text: 'No partner labs found.' };
      const lines = items.slice(0, 8).map((p: { id: string; name: string; city?: string; type?: string; overallScore?: number }) =>
        `• **${p.name}**${p.city ? ` · ${p.city}` : ''}${p.overallScore != null ? ` · Score: ${Math.round(p.overallScore)}%` : ''}`
      );
      return {
        text: `**${r.data?.meta?.total ?? items.length} Partner Labs:**\n\n${lines.join('\n')}`,
        actions: [{ label: 'Partners', route: '/partners' }],
      };
    },
  },

  // ── Equipment ─────────────────────────────────────────────────────────────
  {
    patterns: [/equipment.*list|list.*equipment|all.*equipment|how many.*equipment/i],
    fetch: async () => {
      const r = await api.get('/equipment/utilisation');
      const data = r.data?.data ?? {};
      const types = Object.entries(data as Record<string, { q1: number; q2: number; q3: number }>);
      if (!types.length) return { text: 'No equipment utilisation data found.' };
      const lines = types.map(([name, u]) => {
        const avg = Math.round((u.q1 + u.q2 + u.q3) / 3);
        const bar = avg >= 75 ? '🟢' : avg >= 50 ? '🟡' : '🔴';
        return `• ${bar} **${name}** · Avg Utilisation: **${avg}%** (Q1: ${u.q1}% / Q2: ${u.q2}% / Q3: ${u.q3}%)`;
      });
      return {
        text: `**Equipment Utilisation by Type:**\n\n${lines.join('\n')}`,
        actions: [{ label: 'Equipment module', route: '/equipment' }],
      };
    },
  },
];

// ── Static knowledge base ─────────────────────────────────────────────────────
const KB: { patterns: RegExp[]; answer: string; actions?: Message['actions'] }[] = [
  {
    patterns: [/\bhello\b|\bhi\b|\bhey\b|\bgreet/i],
    answer: "Hello! I'm **Kairoz Assistant** — your guide to the Quantum Kairoz platform.\n\nAsk me about live data (open CAPAs, audit scores, expiring certs, batches on hold…) or any module. How can I help?",
    actions: [
      { label: 'Platform summary', query: 'platform overview' },
      { label: 'Open CAPAs', query: 'show open capas' },
      { label: 'Upcoming events', query: 'upcoming events' },
      { label: 'Expiring certifications', query: 'expiring certifications' },
    ],
  },
  {
    patterns: [/\ball module|\bwhat.*(modules|features|sections)\b/i],
    answer: `**Quantum Kairoz** modules:\n\n**Core Intelligence:** Dashboard, Lab Registry, Equipment, Certifications, Partners, Lab Finder\n\n**Quality Management:** CAPA/Deviations, Audit Tracker, SOP Management, Batch Records, Training, Quality Metrics\n\n**LIMS:** Samples, Specifications, Worksheets, Certificate of Analysis, Stability Studies, QC Control Charts\n\n**Lab Operations:** Documents (DMS), Test Requests, Workflows, Change Control, Calibration & PM`,
  },
  {
    patterns: [/\bdashboard\b/i],
    answer: "The **Executive Dashboard** shows:\n\n• KPI Cards (Active labs, Open CAPAs, Cert alerts, Batches on hold)\n• CAPA Monthly Trend with drill-down\n• Certification Health Donut\n• **Quality Compliance Radar** — 5-axis quality score\n• **Lab Performance Leaderboard** — composite ranking\n• Equipment Distribution\n• Upcoming Events (next 30 days)\n• Partner Lab Map\n\nTry asking: _'show platform summary'_ for live KPI data.",
    actions: [
      { label: 'Platform KPI summary', query: 'platform overview' },
      { label: 'Go to Dashboard', route: '/dashboard' },
    ],
  },
  {
    patterns: [/\bcapa\b|\bdevia(tion|tions)\b|\bcorrective\b|\bpreventive\b/i],
    answer: "**CAPA / Deviations** tracks corrective and preventive actions.\n\nAsk me for live data:\n• _'show open CAPAs'_\n• _'overdue CAPAs'_\n• _'critical CAPAs'_\n\nOr browse the module directly.",
    actions: [
      { label: 'Show open CAPAs', query: 'show open capas' },
      { label: 'Overdue CAPAs', query: 'overdue capas' },
      { label: 'CAPA module', route: '/capa' },
    ],
  },
  {
    patterns: [/\baudit\b|\bgmp.*(check|compliance|inspect)/i],
    answer: "**Audit Tracker** schedules and analyses audits. Scores ≥ 80 pass GMP threshold.\n\nAsk me:\n• _'recent audits'_\n• _'failed audits'_\n• _'scheduled audits'_",
    actions: [
      { label: 'Recent audits', query: 'recent audits' },
      { label: 'Failed audits', query: 'audits below threshold' },
      { label: 'Audit module', route: '/audits' },
    ],
  },
  {
    patterns: [/\bsop\b|\bstandard operating\b|\bprocedure\b/i],
    answer: "**SOP Management** handles SOP lifecycle: Draft → Active → Under Review → Retired.\n\nAsk me:\n• _'SOPs due for review'_\n• _'how many SOPs are there'_",
    actions: [
      { label: 'SOPs due for review', query: 'sops due for review' },
      { label: 'SOP summary', query: 'list all sops' },
      { label: 'SOP module', route: '/sop' },
    ],
  },
  {
    patterns: [/\bbatch\b|\bbmr\b|\bmanufacturing record\b/i],
    answer: "**Batch Records (BMR)** tracks production batches from manufacture to release.\n\nAsk me:\n• _'batches on hold'_\n• _'recently released batches'_\n• _'batch summary'_",
    actions: [
      { label: 'Batches on hold', query: 'batches on qc hold' },
      { label: 'Released batches', query: 'recently released batches' },
      { label: 'BMR module', route: '/bmr' },
    ],
  },
  {
    patterns: [/\bcertif(ication|icate)\b|\bnabl\b|\biso\b|\bwho.gmp\b|\baccreditation\b/i],
    answer: "**Certifications** manages lab accreditations (NABL, ISO 17025, WHO-GMP, FDA).\n\nAsk me:\n• _'expiring certifications'_\n• _'certification summary'_",
    actions: [
      { label: 'Expiring certs', query: 'expiring certifications' },
      { label: 'Cert summary', query: 'valid certifications' },
      { label: 'Certifications module', route: '/certifications' },
    ],
  },
  {
    patterns: [/\bequipment\b|\binstrument\b|\bhplc\b|\bgc\b|\bspectro/i],
    answer: "**Equipment** manages instruments and their calibration/qualification status.\n\nAsk me: _'equipment utilisation'_ for live stats.",
    actions: [
      { label: 'Equipment utilisation', query: 'list all equipment' },
      { label: 'Equipment module', route: '/equipment' },
    ],
  },
  {
    patterns: [/\bdms\b|\bdocument management\b/i],
    answer: "**DMS** is the document repository for SOPs, Protocols, Reports, Certificates, Policies.\n\nAsk me: _'document summary'_ or _'draft documents'_ for live data.",
    actions: [
      { label: 'Document summary', query: 'all documents' },
      { label: 'Draft documents', query: 'draft documents' },
      { label: 'DMS module', route: '/dms' },
    ],
  },
  {
    patterns: [/\bpartner\b|\boutsorc|\bcontract lab\b/i],
    answer: "**Partners** manages external contract labs and their performance.\n\nAsk me: _'list all partners'_ for live data.",
    actions: [
      { label: 'List partners', query: 'list all partners' },
      { label: 'Partners module', route: '/partners' },
    ],
  },
  {
    patterns: [/\bnotif(ication|ications)\b|\balert\b/i],
    answer: "**Notifications** surface time-critical events. Ask me: _'show notifications'_ for current unread alerts.",
    actions: [
      { label: 'Show notifications', query: 'show notifications' },
      { label: 'Notifications page', route: '/notifications' },
    ],
  },
  {
    patterns: [/\btraining\b|\bcompetency\b/i],
    answer: "**Training & Competency** manages staff qualifications and GMP training records with completion tracking and assessment scoring.",
    actions: [{ label: 'Training module', route: '/training' }],
  },
  {
    patterns: [/\blims\b|\bsample\b|\btest request\b|\bworksheet\b/i],
    answer: "**LIMS** covers Samples, Test Requests, Worksheets, CoA generation, Stability Studies, and QC Control Charts. All data is ALCOA+ compliant.",
    actions: [
      { label: 'Sample Tracking', route: '/lims/samples' },
      { label: 'QC Control Charts', route: '/lims/qc-charts' },
    ],
  },
  {
    patterns: [/\bquality metric\b|\bkpi\b|\bperformance indicator/i],
    answer: "**Quality Metrics** provides yield trends, defect categories, TAT distribution, and test volume analytics per lab.",
    actions: [{ label: 'Quality Metrics', route: '/quality-metrics' }],
  },
  {
    patterns: [/\bexport\b|\bdownload\b|\bpdf\b|\bxlsx\b|\breport/i],
    answer: "All records support **PDF** and **XLSX** export generated client-side. The **Report Builder** lets you compose custom multi-module reports.",
    actions: [{ label: 'Report Builder', route: '/report-builder' }],
  },
  {
    patterns: [/\brole\b|\bpermission\b|\baccess\b|\buser management\b/i],
    answer: "**Roles:** admin · qa_director · lab_head · qc_analyst · viewer. Manage users and role assignments from the User Management page.",
    actions: [{ label: 'User Management', route: '/user-management' }],
  },
  {
    patterns: [/\bwhat.*(platform|system|app|software)\b|\bscimplify\b|\bkairoz\b/i],
    answer: "**Quantum Kairoz** by Forge Quantum Solutions is an integrated **LIMS-QMS platform** for pharmaceutical and analytical labs. It meets WHO-GMP, NABL, ISO 17025, ICH, FDA 21 CFR Part 11, and ALCOA+ requirements.\n\nAsk me _'platform overview'_ for live KPI data.",
    actions: [
      { label: 'Live KPI summary', query: 'platform overview' },
      { label: 'Dashboard', route: '/dashboard' },
    ],
  },
  {
    patterns: [/\bhelp\b|\bwhat can you\b|\bwhat do you\b/i],
    answer: "I can answer questions about **live platform data** and explain any module. Try:",
    actions: [
      { label: 'Platform KPI summary', query: 'platform overview' },
      { label: 'Open CAPAs', query: 'show open capas' },
      { label: 'Upcoming events', query: 'upcoming events' },
      { label: 'Expiring certs', query: 'expiring certifications' },
      { label: 'Batches on hold', query: 'batches on qc hold' },
      { label: 'Recent audits', query: 'recent audits' },
      { label: 'SOPs due for review', query: 'sops due for review' },
      { label: 'All modules', query: 'show all modules' },
    ],
  },
];

const FALLBACK = "I don't have specific information on that yet. Try asking about live data like:\n• _'open CAPAs'_  _'expiring certifications'_  _'batches on hold'_  _'recent audits'_\n\nOr type **help** to see all topics.";

// ── Markdown-like renderer ────────────────────────────────────────────────────
function RenderText({ text }: { text: string }) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return (
    <>
      {parts.map((p, i) =>
        p.startsWith('**') && p.endsWith('**')
          ? <strong key={i}>{p.slice(2, -2)}</strong>
          : p.split('\n').map((line, j, arr) => (
              <span key={`${i}-${j}`}>{line}{j < arr.length - 1 && <br />}</span>
            ))
      )}
    </>
  );
}

const QUICK_CHIPS: Message['actions'] = [
  { label: 'Open CAPAs', query: 'show open capas' },
  { label: 'Expiring certs', query: 'expiring certifications' },
  { label: 'Batches on hold', query: 'batches on qc hold' },
  { label: 'Upcoming events', query: 'upcoming events' },
  { label: 'Platform KPIs', query: 'platform overview' },
  { label: 'Recent audits', query: 'recent audits' },
];

let msgCounter = 0;
const mkId = () => `msg-${++msgCounter}`;

export default function ChatbotWidget() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      id: mkId(),
      role: 'assistant',
      text: "👋 Hi! I'm **Kairoz Assistant** — I can answer questions about live data in the platform (open CAPAs, audit scores, expiring certs, batches on hold…) or explain any module.",
      timestamp: new Date(),
      actions: QUICK_CHIPS,
    },
  ]);
  const [input, setInput] = useState('');
  const [typing, setTyping] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef  = useRef<HTMLInputElement>(null);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, typing]);
  useEffect(() => { if (open) setTimeout(() => inputRef.current?.focus(), 200); }, [open]);

  const addAssistantMsg = useCallback((text: string, isData = false, actions?: Message['actions']) => {
    setTyping(false);
    setMessages((m) => [...m, { id: mkId(), role: 'assistant', text, timestamp: new Date(), isData, actions }]);
  }, []);

  const sendMessage = useCallback(async (text: string) => {
    if (!text.trim()) return;
    setMessages((m) => [...m, { id: mkId(), role: 'user', text: text.trim(), timestamp: new Date() }]);
    setInput('');
    setTyping(true);

    // Simulate thinking delay
    await new Promise((r) => setTimeout(r, 350 + Math.random() * 300));

    // 1 — Try data intents first
    const dataIntent = DATA_INTENTS.find((d) => d.patterns.some((p) => p.test(text)));
    if (dataIntent) {
      try {
        const result = await dataIntent.fetch();
        addAssistantMsg(result.text, true, result.actions);
        return;
      } catch (_e) {
        addAssistantMsg('⚠️ Could not fetch live data right now. Please try again shortly.', false);
        return;
      }
    }

    // 2 — Fall back to static knowledge base
    const kb = KB.find((entry) => entry.patterns.some((p) => p.test(text)));
    if (kb) {
      addAssistantMsg(kb.answer, false, kb.actions);
      return;
    }

    // 3 — Generic fallback
    addAssistantMsg(FALLBACK, false, [
      { label: 'Open CAPAs', query: 'show open capas' },
      { label: 'Platform KPIs', query: 'platform overview' },
    ]);
  }, [addAssistantMsg]);

  const handleAction = (action: { label: string; route?: string; query?: string }) => {
    if (action.route) { navigate(action.route); setOpen(false); }
    else if (action.query) { sendMessage(action.query); }
  };

  const resetChat = () => {
    setMessages([{
      id: mkId(), role: 'assistant',
      text: "Chat reset. Ask me about live platform data or any module!",
      timestamp: new Date(), actions: QUICK_CHIPS,
    }]);
    setInput('');
  };

  return (
    <>
      <div style={{ position: 'fixed', bottom: 28, right: 28, zIndex: 9000 }}>
        {/* Floating button */}
        <AnimatePresence>
          {!open && (
            <motion.button
              initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0, opacity: 0 }}
              whileHover={{ scale: 1.08 }} whileTap={{ scale: 0.94 }}
              onClick={() => setOpen(true)}
              style={{
                width: 56, height: 56, borderRadius: '50%',
                background: `linear-gradient(135deg, ${GOLD}, #8a5e10)`,
                border: 'none', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: `0 4px 20px rgba(176,125,26,0.45), 0 2px 8px rgba(0,0,0,0.2)`,
                color: '#fff', position: 'relative',
              }}
            >
              <MessageCircle size={24} />
              <span style={{
                position: 'absolute', inset: -4, borderRadius: '50%',
                border: `2px solid ${GOLD}`, animation: 'kairoz-pulse 2s ease-out infinite',
                pointerEvents: 'none',
              }} />
            </motion.button>
          )}
        </AnimatePresence>

        {/* Chat panel */}
        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.95 }}
              transition={{ type: 'spring', stiffness: 340, damping: 28 }}
              style={{
                position: 'absolute', bottom: 0, right: 0,
                width: 400, height: 560,
                background: '#fff', borderRadius: 16,
                boxShadow: '0 20px 60px rgba(0,0,0,0.18), 0 4px 16px rgba(0,0,0,0.08)',
                display: 'flex', flexDirection: 'column', overflow: 'hidden',
                border: '1px solid rgba(0,0,0,0.07)',
              }}
            >
              {/* Header */}
              <div style={{
                padding: '14px 16px',
                background: `linear-gradient(135deg, ${GOLD}, #8a5e10)`,
                display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0,
              }}>
                <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Bot size={18} color="#fff" />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 14, color: '#fff', letterSpacing: '0.02em' }}>Kairoz Assistant</div>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.75)', display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Database size={9} color="rgba(255,255,255,0.75)" />
                    Live platform data · Quantum Kairoz
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 4 }}>
                  <button onClick={resetChat} title="Reset chat"
                    style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 8, width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#fff' }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.25)')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.15)')}
                  ><RotateCcw size={13} /></button>
                  <button onClick={() => setOpen(false)} title="Close"
                    style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 8, width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#fff' }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.25)')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.15)')}
                  ><X size={14} /></button>
                </div>
              </div>

              {/* Messages */}
              <div style={{ flex: 1, overflowY: 'auto', padding: '12px 12px 4px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                {messages.map((msg) => (
                  <div key={msg.id} style={{ display: 'flex', flexDirection: 'column', alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start' }}>
                    {msg.role === 'assistant' && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 4 }}>
                        <div style={{ width: 20, height: 20, borderRadius: '50%', background: `linear-gradient(135deg, ${GOLD}, #8a5e10)`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Sparkles size={10} color="#fff" />
                        </div>
                        <span style={{ fontSize: 10, fontWeight: 600, color: GOLD }}>Kairoz</span>
                        {msg.isData && (
                          <span style={{ fontSize: 9, background: '#EFF6FF', color: '#2563EB', border: '1px solid #BFDBFE', borderRadius: 100, padding: '1px 6px', fontWeight: 600 }}>
                            live data
                          </span>
                        )}
                        <span style={{ fontSize: 9, color: '#94A3B8' }}>{msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                      </div>
                    )}
                    <div style={{
                      maxWidth: '90%',
                      padding: '9px 12px',
                      borderRadius: msg.role === 'user' ? '14px 14px 4px 14px' : '4px 14px 14px 14px',
                      background: msg.role === 'user' ? `linear-gradient(135deg, ${GOLD}, #8a5e10)` : '#F8F9FC',
                      color: msg.role === 'user' ? '#fff' : '#1a1a2e',
                      fontSize: 12.5, lineHeight: 1.6,
                      border: msg.role === 'assistant' ? '1px solid rgba(0,0,0,0.05)' : 'none',
                      boxShadow: msg.role === 'user' ? `0 2px 8px rgba(176,125,26,0.3)` : '0 1px 3px rgba(0,0,0,0.04)',
                    }}>
                      <RenderText text={msg.text} />
                    </div>
                    {msg.role === 'user' && (
                      <span style={{ fontSize: 9, color: '#94A3B8', marginTop: 2 }}>{msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                    )}

                    {/* Action chips */}
                    {msg.actions && msg.actions.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 6, maxWidth: '92%' }}>
                        {msg.actions.map((a) => (
                          <button key={a.label} onClick={() => handleAction(a)}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 4,
                              padding: '4px 10px', borderRadius: 100,
                              background: '#fff',
                              border: `1px solid ${GOLD}50`,
                              fontSize: 11, fontWeight: 500, color: GOLD,
                              cursor: 'pointer', transition: 'all 0.15s',
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = `${GOLD}10`; e.currentTarget.style.borderColor = GOLD; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.borderColor = `${GOLD}50`; }}
                          >
                            {a.route && <ChevronRight size={10} />}
                            {a.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}

                {/* Typing indicator */}
                {typing && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                    <div style={{ width: 20, height: 20, borderRadius: '50%', background: `linear-gradient(135deg, ${GOLD}, #8a5e10)`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Sparkles size={10} color="#fff" />
                    </div>
                    <div style={{ padding: '8px 12px', background: '#F8F9FC', borderRadius: '4px 14px 14px 14px', border: '1px solid rgba(0,0,0,0.05)' }}>
                      <div style={{ display: 'flex', gap: 3, alignItems: 'center', height: 14 }}>
                        {[0, 1, 2].map((i) => (
                          <span key={i} style={{
                            width: 6, height: 6, borderRadius: '50%', background: '#94A3B8',
                            animation: `kairoz-bounce 1.2s ease-in-out ${i * 0.2}s infinite`,
                            display: 'inline-block',
                          }} />
                        ))}
                      </div>
                    </div>
                  </div>
                )}
                <div ref={bottomRef} />
              </div>

              {/* Input */}
              <div style={{ padding: '10px 12px 12px', borderTop: '1px solid rgba(0,0,0,0.06)', flexShrink: 0, background: '#fafafa' }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input
                    ref={inputRef}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(input); } }}
                    placeholder="Ask about live data or any module…"
                    style={{
                      flex: 1, padding: '9px 13px', borderRadius: 10,
                      border: '1.5px solid rgba(0,0,0,0.1)',
                      fontSize: 12.5, color: '#1a1a2e',
                      background: '#fff', outline: 'none',
                      transition: 'border-color 0.15s',
                    }}
                    onFocus={(e) => (e.currentTarget.style.borderColor = GOLD)}
                    onBlur={(e) => (e.currentTarget.style.borderColor = 'rgba(0,0,0,0.1)')}
                  />
                  <button
                    onClick={() => sendMessage(input)}
                    disabled={!input.trim() || typing}
                    style={{
                      width: 36, height: 36, borderRadius: 10, flexShrink: 0,
                      background: input.trim() && !typing ? `linear-gradient(135deg, ${GOLD}, #8a5e10)` : '#e5e7eb',
                      border: 'none', cursor: input.trim() && !typing ? 'pointer' : 'default',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: input.trim() && !typing ? '#fff' : '#9ca3af',
                      transition: 'all 0.15s',
                    }}
                  >
                    <Send size={14} />
                  </button>
                </div>
                <p style={{ fontSize: 9.5, color: '#94A3B8', marginTop: 5, textAlign: 'center' }}>
                  Quantum Kairoz · Forge Quantum Solutions
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <style>{`
        @keyframes kairoz-pulse {
          0%   { opacity: 0.7; transform: scale(1); }
          70%  { opacity: 0;   transform: scale(1.5); }
          100% { opacity: 0;   transform: scale(1.5); }
        }
        @keyframes kairoz-bounce {
          0%, 80%, 100% { transform: translateY(0); opacity: 0.5; }
          40%            { transform: translateY(-4px); opacity: 1; }
        }
      `}</style>
    </>
  );
}

// suppress unused color warnings — referenced in data handlers above
void RED; void GREEN; void ORANGE;
